// Astarte-on-glitch stamp support.
// Stamps are keyword-to-image replacements (e.g. :おはよう: or 熱盛) rendered
// client-side from the Astarte media CDN, ported from the old fork's
// emojify_astarte().

import type { FC, ReactNode } from 'react';

const STAMP_HOST = 'https://astarte.global.ssl.fastly.net/emoji';

interface StampDefinition {
  re: RegExp;
  file: string;
  height?: string;
}

// Order matters: longer/more specific patterns (e.g. :ふたば_おはよう:,
// :花束3:, :4周年1:) must match before their shorter prefixes.
const STAMPS: StampDefinition[] = [
  { re: /5,?000\s*兆円/, file: '5000tyoen.svg', height: '1.8em' },
  { re: /熱盛/, file: 'atumori.svg', height: '2.5em' },
  { re: /バジリスク\s*タイム/, file: 'basilisktime.svg', height: '2.5em' },
  { re: /欲しい！/, file: 'hosii.svg', height: '1.7em' },
  { re: /ささやき(?:たいまー|タイマー)/, file: 'in_to_the_dark.svg' },
  { re: /:ロケット:/, file: 'rocket.gif' },
  { re: /:ふたば_?おはよう:/, file: 'hutaba.png' },
  { re: /:おはよう:/, file: 'morning.gif' },
  { re: /:かえりたい:/, file: 'kaeritai.jpeg' },
  { re: /:はらへり:/, file: 'haraheri.png' },
  { re: /:かなしい:/, file: 'kanashi.png' },
  { re: /:うれしい:/, file: 'ureshi.png' },
  { re: /:大喜利:/, file: 'ogiri.jpeg' },
  { re: /:ヘディング:/, file: 'heading.gif' },
  { re: /:じゃんけん:/, file: 'janken.gif' },
  { re: /:おやすみ:/, file: 'good_night.gif' },
  { re: /:ごはん:/, file: 'gohan.png' },
  { re: /:おそよう:/, file: 'osoyou.png' },
  { re: /:ありがとう:/, file: 'arigatou.gif' },
  { re: /:ルーレット:/, file: 'roulette.gif' },
  { re: /:働きたくない:/, file: 'notWork.jpg' },
  { re: /:起床Chance:/, file: '750746c7bd3a0edc.png' },
  { re: /:入眠Rush:/, file: '80bff0490a51d517.png' },
  { re: /:眠気襲来:/, file: '45bec8a22a29e958.png' },
  { re: /:みぷ:/, file: 'd000e5d479ce618c.png' },
  { re: /:全裸報告:/, file: '2f18e130e4bfb19b.png' },
  { re: /:NFC襲来:/, file: '6537d027313fd066.png' },
  { re: /:椅子転発生:/, file: '889f2f40ede5d058.png' },
  { re: /:ねれない:/, file: 'nerenine.gif' },
  { re: /:配信します:/, file: 'haishin.gif' },
  { re: /:ぴえん:/, file: 'pien.jpg' },
  { re: /:踊る:/, file: 'odoru.jpg' },
  { re: /:わびる:/, file: 'wabiru.jpg' },
  { re: /:よろこぶ:/, file: 'yorokobu.jpg' },
  { re: /:とどかない:/, file: 'todokanai.jpg' },
  { re: /:こしかける:/, file: 'kosikakeru.jpg' },
  { re: /:52000rpm:/, file: '52000rpm.gif' },
  { re: /:花束3:/, file: 'hanataba3.png' },
  { re: /:花束2:/, file: 'hanataba2.png' },
  { re: /:花束:/, file: 'hanataba.png' },
  { re: /:4周年1:/, file: '4years1.png' },
  { re: /:4周年2:/, file: '4years2.png' },
  { re: /:4周年3:/, file: '4years3.png' },
  { re: /:4周年:/, file: '4years.png' },
  { re: /:スクワット:/, file: 'sukuwatto.jpg' },
  { re: /:(?:筋|キン|きん)(?:トレ|とれ):/, file: 'kintore.gif' },
  { re: /:懸垂:/, file: 'kensui.png' },
  { re: /:ベンチプレス:/, file: 'bentipuresu.png' },
  { re: /:アームカール:/, file: 'a-muka-ru.png' },
  { re: /:プロテイン1:/, file: 'syakasyaka.gif' },
  { re: /:プロテイン:/, file: 'syakasyaka2.gif' },
  { re: /:ポージング:/, file: 'guttu.png' },
  { re: /:(?:よし|ヨシ)(?:!|！):/, file: '120703.gif' },
];

const ANY_STAMP_RE = new RegExp(
  STAMPS.map((stamp) => `(?:${stamp.re.source})`).join('|'),
);

export interface StampMatch {
  matched: string;
  file: string;
  height?: string;
}

export function stampUrl(file: string): string {
  return `${STAMP_HOST}/${file}`;
}

// Splits text into plain segments and stamp matches. Returns null when the
// text contains no stamps at all, so callers can skip extra work.
export function splitTextByStamps(
  text: string,
): (string | StampMatch)[] | null {
  if (!ANY_STAMP_RE.test(text)) {
    return null;
  }

  const parts: (string | StampMatch)[] = [];
  let rest = text;

  while (rest.length > 0) {
    let earliest: {
      index: number;
      length: number;
      stamp: StampDefinition;
    } | null = null;

    for (const stamp of STAMPS) {
      const match = stamp.re.exec(rest);
      if (
        match &&
        (earliest === null ||
          match.index < earliest.index ||
          (match.index === earliest.index && match[0].length > earliest.length))
      ) {
        earliest = { index: match.index, length: match[0].length, stamp };
      }
    }

    if (!earliest) {
      parts.push(rest);
      break;
    }

    if (earliest.index > 0) {
      parts.push(rest.slice(0, earliest.index));
    }

    parts.push({
      matched: rest.slice(earliest.index, earliest.index + earliest.length),
      file: earliest.stamp.file,
      height: earliest.stamp.height,
    });

    rest = rest.slice(earliest.index + earliest.length);
  }

  return parts;
}

export const AstarteStamp: FC<{ stamp: StampMatch }> = ({ stamp }) => (
  <img
    src={stampUrl(stamp.file)}
    alt={stamp.matched}
    title={stamp.matched}
    className={stamp.height ? undefined : 'astarte-stamp'}
    style={stamp.height ? { height: stamp.height } : undefined}
    loading='lazy'
  />
);

// Converts text to React nodes, rendering stamps as images and passing the
// remaining plain segments through `renderText` (usually the emoji
// tokenizer). Returns null when no stamp is present.
export function textToStampNodes(
  text: string,
  renderText: (segment: string) => ReactNode,
): ReactNode[] | null {
  const parts = splitTextByStamps(text);

  if (!parts) {
    return null;
  }

  return parts.map((part, index) => {
    if (typeof part === 'string') {
      return renderText(part);
    }
    return <AstarteStamp stamp={part} key={`stamp-${part.file}-${index}`} />;
  });
}

// DOM-based replacement for server-rendered (public) pages: walks the text
// nodes below `element` and swaps stamp keywords for <img> elements.
export function renderStampsInElement(element: Element): void {
  const walker = element.ownerDocument.createTreeWalker(
    element,
    NodeFilter.SHOW_TEXT,
  );
  const textNodes: Text[] = [];

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(node as Text);
  }

  for (const node of textNodes) {
    const parts = splitTextByStamps(node.data);
    if (!parts) {
      continue;
    }

    const fragment = element.ownerDocument.createDocumentFragment();
    for (const part of parts) {
      if (typeof part === 'string') {
        fragment.append(part);
      } else {
        const img = element.ownerDocument.createElement('img');
        img.src = stampUrl(part.file);
        img.alt = part.matched;
        img.title = part.matched;
        img.loading = 'lazy';
        if (part.height) {
          img.style.height = part.height;
        } else {
          img.className = 'astarte-stamp';
        }
        fragment.append(img);
      }
    }

    node.replaceWith(fragment);
  }
}
